import React, { useState, useEffect, useCallback } from 'react';
import { usePayment } from '../../context/PaymentContext';
import { useAuth } from '../../context/AuthContext';
import ApprovalWorkflow from '../../components/admin/ApprovalWorkflow';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Work as WorkIcon,
  TrendingUp as TrendingUpIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';

// Removed unused COLORS constant

const Dashboard = () => {
  const { getAllTransactions } = usePayment();
  const { currentUser, apiCall } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    totalJobs: 0,
    totalServices: 0,
    totalAdmins: 0,
    pendingApprovals: 0,
    recentActivities: [],
    systemHealth: 'good'
  });
  const [revenueStats, setRevenueStats] = useState({
    totalRevenue: 0,
    jobPostingRevenue: 0,
    serviceBookingRevenue: 0,
    monthlyRevenue: {},
  });

  const loadTransactions = useCallback(async () => {
    try {
      const transactionList = await getAllTransactions();
      calculateRevenueStats(transactionList);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  }, [getAllTransactions]);

  const loadSystemStats = useCallback(async () => {
    try {
      const response = await apiCall('/admin/dashboard/stats', {
        method: 'GET'
      });

      if (response.success) {
        const stats = response.stats || response.data?.stats || {};
        setDashboardStats(prev => ({
          ...prev,
          totalUsers: Number(stats.totalUsers || 0),
          totalJobs: Number(stats.totalJobs || 0),
          totalServices: Number(stats.totalServices || 0),
          pendingApprovals: Number(stats.pendingApprovals || 0),
        }));
      }
    } catch (error) {
      console.error('Error loading system stats:', error);
      // Fallback to mock data if API fails
      setDashboardStats(prev => ({
        ...prev,
        totalUsers: 0,
        totalJobs: 0,
        totalServices: 0,
        pendingApprovals: 0,
        systemHealth: 'good'
      }));
    }
  }, [apiCall]);

  const loadRecentActivities = useCallback(async () => {
    try {
      const response = await apiCall('/audit/recent', {
        method: 'GET',
        params: { limit: 10 }
      });

      if (response.success) {
        setDashboardStats(prev => ({
          ...prev,
          recentActivities: response.data.logs || []
        }));
      }
    } catch (error) {
      console.error('Error loading recent activities:', error);
      // Fallback to mock data
      setDashboardStats(prev => ({
        ...prev,
        recentActivities: [
          {
            adminEmail: 'john.doe@admin.com',
            action: 'CREATE_ADMIN',
            targetType: 'Admin',
            success: true,
            createdAt: new Date().toISOString(),
            details: { targetName: 'New Admin User' }
          },
          {
            adminEmail: 'jane.smith@admin.com',
            action: 'APPROVE_JOB',
            targetType: 'Job',
            success: true,
            createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            details: { targetName: 'Senior Developer Position' }
          },
          {
            adminEmail: 'mike.johnson@admin.com',
            action: 'APPROVE_SERVICE',
            targetType: 'Service',
            success: true,
            createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
            details: { targetName: 'Web Development Service' }
          }
        ]
      }));
    }
  }, [apiCall]);

  const loadAdminCounts = useCallback(async () => {
    try {
      const response = await apiCall('/admin-management', {
        method: 'GET',
        params: { page: 1, limit: 1 }
      });

      if (response.success) {
        const total = response.pagination?.total || response.data?.pagination?.total || 0;
        setDashboardStats(prev => ({
          ...prev,
          totalAdmins: total
        }));
      }
    } catch (error) {
      console.error('Error loading admin counts:', error);
    }
  }, [apiCall]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadTransactions(),
        loadSystemStats(),
        loadRecentActivities(),
        loadAdminCounts()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [loadTransactions, loadSystemStats, loadRecentActivities, loadAdminCounts]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);


  const calculateRevenueStats = (transactionList) => {
    const monthlyRevenue = {};
    const stats = transactionList.reduce(
      (acc, transaction) => {
        const amount = transaction.commission || transaction.amount || 0;
        const date = new Date(transaction.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        // Update monthly revenue
        monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + amount;

        // Update revenue by type
        acc.totalRevenue += amount;
        if (transaction.type === 'jobPosting') {
          acc.jobPostingRevenue += amount;
        } else if (transaction.type === 'serviceBooking') {
          acc.serviceBookingRevenue += amount;
        }

        return acc;
      },
      {
        totalRevenue: 0,
        jobPostingRevenue: 0,
        serviceBookingRevenue: 0,
      }
    );

    setRevenueStats({
      ...stats,
      monthlyRevenue,
    });
  };

  const monthlyRevenueData = Object.entries(revenueStats.monthlyRevenue)
    .map(([month, amount]) => ({
      month,
      revenue: Number(amount.toFixed(2)),
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

  // Pie chart segments can be derived inline when rendering if needed

  const getGrowthPercentage = () => {
    if (monthlyRevenueData.length < 2) return '+0%';
    const current = monthlyRevenueData[monthlyRevenueData.length - 1]?.revenue || 0;
    const previous = monthlyRevenueData[monthlyRevenueData.length - 2]?.revenue || 0;
    if (previous === 0) return '+0%';
    const growth = ((current - previous) / previous * 100).toFixed(1);
    return `${growth > 0 ? '+' : ''}${growth}%`;
  };

  const stats = [
    { 
      title: 'Total Users', 
      value: dashboardStats.totalUsers.toLocaleString(), 
      icon: <PersonIcon />, 
      color: '#cf1130',
      change: '+5.2%'
    },
    { 
      title: 'Active Jobs', 
      value: dashboardStats.totalJobs.toLocaleString(), 
      icon: <WorkIcon />, 
      color: '#2e7d32',
      change: '+12.3%'
    },
    { 
      title: 'Revenue Growth', 
      value: getGrowthPercentage(), 
      icon: <TrendingUpIcon />, 
      color: '#9c27b0',
      change: 'This month'
    },
  ];

  const getActionIcon = (action) => {
    if (action.includes('CREATE') || action.includes('APPROVE')) return <CheckCircleIcon color="success" />;
    if (action.includes('DELETE') || action.includes('REJECT')) return <ErrorIcon color="error" />;
    if (action.includes('UPDATE') || action.includes('ASSIGN')) return <WarningIcon color="warning" />;
    return <ViewIcon color="info" />;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Admin Dashboard
        </Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={loadDashboardData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Chip 
            icon={<SecurityIcon />}
            label={`Welcome, ${currentUser?.firstName || 'Admin'}`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Dashboard Overview" />
          <Tab label="Approval Workflow" />
          <Tab label="System Analytics" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Stats Cards */}
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={`stat-${index}`}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography color="text.secondary" gutterBottom variant="body2">
                        {stat.title}
                      </Typography>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                        {stat.change}
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        bgcolor: stat.color,
                        width: 56,
                        height: 56,
                      }}
                    >
                      {stat.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* System Health & Quick Actions */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Health
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body1">All Systems Operational</Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<AdminIcon />}>
                    Manage Admins
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<SecurityIcon />}>
                    View Audit Logs
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<AssessmentIcon />}>
                    Generate Report
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Admin Activities */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Recent Admin Activities
              </Typography>
              <List>
                {dashboardStats.recentActivities.slice(0, 5).map((activity, index) => (
                  <ListItem key={`activity-${index}`} divider={index !== dashboardStats.recentActivities.length - 1}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: activity.success ? 'success.main' : 'error.main' }}>
                        {getActionIcon(activity.action)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" component="span">
                            {activity.adminEmail}
                          </Typography>
                          <Chip 
                            label={activity.action.replace('_', ' ')} 
                            size="small" 
                            color={activity.success ? 'success' : 'error'}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" component="span">
                            {activity.targetType}: {activity.details?.targetName || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
                            {formatTimeAgo(activity.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Platform Overview */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Platform Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Admins
                  </Typography>
                  <Typography variant="h4">{dashboardStats.totalAdmins}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pending Approvals
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {dashboardStats.pendingApprovals}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Success Rate
                  </Typography>
                  <Typography variant="h4" color="success.main">98.5%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    ${revenueStats.totalRevenue.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Revenue Chart */}
          {monthlyRevenueData.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Monthly Revenue Trend
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={monthlyRevenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="revenue" stroke="#cf1130" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {tabValue === 1 && <ApprovalWorkflow />}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                System Analytics
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Detailed analytics and reporting features will be available here.
                This includes user engagement metrics, system performance data, and business intelligence reports.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Dashboard;